import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, Image as ImageIcon, Camera } from "lucide-react";
import { API, auth, mediaUrl, inputClass, labelClass, fileClass, primaryBtn, handleAuthError } from "./shared";
import { useConfirm } from "./useConfirm";

const CATEGORIES = [
  { id: "entreprise", label: "L'Entreprise (locaux, équipe, événements)" },
  { id: "mecanique", label: "Mécanique & Diagnostic" },
  { id: "tolerie", label: "Tôlerie & Peinture" },
  { id: "tapisserie", label: "Tapisserie & Intérieur" },
  { id: "formation", label: "Formation & Jeunes (page Nous rejoindre)" },
];

const AdminPhotos = ({ token, logout }) => {
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("entreprise");
  const [uploading, setUploading] = useState(false);
  const { confirm, dialog } = useConfirm();

  const load = () => axios.get(`${API}/gallery`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choisissez une image");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      fd.append("category", category);
      await axios.post(`${API}/gallery`, fd, auth(token));
      toast.success("Photo ajoutée à la galerie !");
      setFile(null);
      setCaption("");
      document.getElementById("admin-file-input").value = "";
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de l'envoi. Réessayez."));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (item) => {
    const ok = await confirm({ title: "Supprimer cette photo ?", description: `« ${item.caption || "Sans légende"} » sera retirée de la galerie publique.` });
    if (!ok) return;
    try {
      await axios.delete(`${API}/gallery/${item.id}`, auth(token));
      toast.success("Photo supprimée");
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de la suppression"));
    }
  };

  return (
    <div data-testid="admin-photos-tab">
      {dialog}
      <form onSubmit={upload} className="rounded-3xl bg-mint p-8" data-testid="admin-upload-form">
        <h2 className="font-display flex items-center gap-2 text-lg font-bold text-forest">
          <Camera className="h-5 w-5 text-gold" />
          Ajouter une photo
        </h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-3">
          <div>
            <label className={labelClass}>Image *</label>
            <input id="admin-file-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files[0])} data-testid="admin-upload-file-input" className={fileClass} />
          </div>
          <div>
            <label className={labelClass}>Légende</label>
            <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Ex : Peinture Mercedes GLE" data-testid="admin-caption-input" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Catégorie</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="admin-category-select" className={inputClass}>
              {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
        </div>
        <button type="submit" disabled={uploading} data-testid="admin-upload-button" className={`mt-6 ${primaryBtn}`}>
          <Upload className="h-4 w-4" />
          {uploading ? "Envoi en cours..." : "Publier la photo"}
        </button>
      </form>

      <h2 className="font-display mt-14 text-lg font-bold text-forest">
        Photos en ligne <span className="text-gold">({items.length})</span>
      </h2>
      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((item, i) => (
          <div key={item.id} className="overflow-hidden rounded-2xl border border-brand/10 bg-white shadow-sm" data-testid={`admin-gallery-item-${i}`}>
            <div className="aspect-[4/3] bg-mint">
              <img src={mediaUrl(item.url)} alt={item.caption} className="h-full w-full object-cover" loading="lazy" />
            </div>
            <div className="flex items-center justify-between gap-2 px-3 py-2.5">
              <span className="truncate text-xs text-forest">{item.caption || "Sans légende"}</span>
              <button onClick={() => remove(item)} data-testid={`admin-delete-button-${i}`} className="shrink-0 text-brand/50 transition-colors hover:text-gold" aria-label="Supprimer">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full flex items-center gap-3 rounded-2xl border-2 border-dashed border-brand/20 px-6 py-10 text-brand">
            <ImageIcon className="h-5 w-5" />
            Aucune photo pour le moment. Ajoutez la première ci-dessus.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPhotos;
